import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SEO from "@/components/SEO";
import { canonical, profilePageSchema } from "@/utils/seo";
import SectionSkeleton from "@/components/base/SectionSkeleton";
import ArticleCard from "@/components/base/ArticleCard";
import { getAuthorBySlug, getPostsByAuthorId } from "@/api/content";
import type { AuthorProfile, PostCard } from "@/types/content";

export default function AuthorDetailPage({ initialData }: { initialData?: AuthorProfile | null }) {
  const { slug } = useParams<{ slug: string }>();
  const [author, setAuthor] = useState<AuthorProfile | null>(initialData ?? null);
  const [posts, setPosts] = useState<PostCard[]>([]);
  const [loading, setLoading] = useState(!initialData);

  useEffect(() => {
    if (!slug) return;
    getAuthorBySlug(slug).then((auth) => {
      if (auth) {
        setAuthor(auth);
        getPostsByAuthorId(auth.id).then((p) => {
          setPosts(p);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });
  }, [slug]);

  if (loading) {
    return <SectionSkeleton type="spinner" />;
  }

  if (!author) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center">
        <h2 className="text-xl font-semibold text-slate-800 mb-2">找不到此作者</h2>
        <a
          href="/authors"
          className="px-5 py-2 bg-primary text-white text-sm font-medium rounded-full hover:bg-primary-dark transition-colors whitespace-nowrap"
        >
          返回作者列表
        </a>
      </div>
    );
  }

  return (
    <>
      <SEO
        meta={{
          title: `${author.name}｜美好時光指南作者`,
          description: `${author.bio} 已發表 ${author.articleCount} 篇評測文章，瀏覽 ${author.name} 的所有作品。`,
          canonical: canonical(`/authors/${author.slug}`),
          keywords: [author.name, "作者", "評測", "美好時光指南"],
          structuredData: profilePageSchema(author.name, author.bio, author.avatar, `/authors/${author.slug}`),
        }}
      />

      {/* Author Header */}
      <div className="w-full bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-14 flex flex-col md:flex-row items-center md:items-start gap-6 md:gap-8">
          <img
            loading="lazy"
            src={author.avatar}
            alt={author.name}
            className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover border-2 border-slate-100 shrink-0"
          />
          <div className="text-center md:text-left">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mb-2">
              {author.name}
            </h1>
            <p className="text-slate-500 mb-4 max-w-xl">{author.bio}</p>
            <div className="flex items-center justify-center md:justify-start gap-4 text-sm text-slate-400">
              <span className="flex items-center gap-1">
                <i className="ri-article-line text-primary" />
                {author.articleCount} 篇文章
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-star-line text-amber-400" />
                {author.reviewCount} 則評價
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Author Posts */}
      <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-slate-800 mb-6 md:mb-8">
            {author.name} 的文章
          </h2>

          {posts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500">此作者暫無文章</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post) => (
                <ArticleCard key={post.id} post={post} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}